import type { OnboardingRun } from '@hostesa/api-client';
import {
  useActivateHotel,
  useApproveRun,
  useDraftItems,
  useEnqueueCrawl,
  useValidateUrl,
} from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Button, EmptyState, ErrorState, LoadingState, StatusBadge } from '@hostesa/ui';
import { ActivationGateChecklist } from './activation-gate-checklist';
import { DraftItemReview } from './draft-item-review';
import { namespace } from './i18n';
import { asText, blockingItemsRemaining, runStatusVariant, runStep } from './lib';

/**
 * OnboardingWizard — the three-step review flow (Connect → Review → Activate)
 * for a single onboarding run. The active step is derived from the run status;
 * the manager validates the URL, kicks the crawl, reviews each draft item, then
 * approves and activates once the activation gate passes.
 */
export function OnboardingWizard({
  tenantId,
  hotelId,
  run,
}: {
  tenantId: string;
  hotelId: string;
  run: OnboardingRun;
}) {
  const { t } = useT(namespace);
  const step = runStep(run.status);

  const validateUrl = useValidateUrl(tenantId, hotelId);
  const crawl = useEnqueueCrawl(tenantId, hotelId);
  const approve = useApproveRun(tenantId, hotelId);
  const activate = useActivateHotel(tenantId, hotelId);

  const itemsQuery = useDraftItems(tenantId, hotelId, run.id);
  const items = itemsQuery.data ?? [];
  const blocking = blockingItemsRemaining(items);
  const statusDetail = asText(run.statusDetail);

  const steps = [t('steps.connect'), t('steps.review'), t('steps.activate')];

  return (
    <div data-testid="onboarding-wizard" className="flex flex-col gap-6">
      {/* Step indicator */}
      <ol data-testid="onboarding-step-indicator" className="flex items-center gap-3">
        {steps.map((label, index) => (
          <li
            key={label}
            data-testid={`onboarding-step-${index}`}
            data-active={index === step ? 'true' : 'false'}
            className="flex items-center gap-2"
          >
            <span
              className={
                index === step
                  ? 'flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-charcoal)] text-xs text-[var(--color-alabaster)]'
                  : 'flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-stone-100)] text-xs text-[var(--color-stone-mid)]'
              }
            >
              {index + 1}
            </span>
            <span
              className={
                index === step
                  ? 'text-sm font-medium text-[var(--color-charcoal)]'
                  : 'text-sm text-[var(--color-stone-mid)]'
              }
            >
              {label}
            </span>
          </li>
        ))}
        <li className="ml-auto">
          <StatusBadge
            data-testid="onboarding-run-status"
            status={runStatusVariant(run.status)}
            label={t(`runStatus.${run.status}`)}
          />
        </li>
      </ol>

      {/* Step 1 — Connect */}
      {step === 0 && (
        <section
          data-testid="onboarding-step-connect"
          className="flex flex-col gap-4 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
        >
          <div className="flex flex-col gap-1">
            <h2 className="font-medium text-[var(--color-charcoal)]">{t('connect.title')}</h2>
            <p className="text-sm text-[var(--color-stone-mid)]">{t('connect.description')}</p>
          </div>

          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-[var(--color-stone-mid)]">{t('connect.pagesQueued')}</dt>
              <dd
                data-testid="onboarding-pages-queued"
                className="text-sm text-[var(--color-charcoal)]"
              >
                {run.pagesQueued}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-stone-mid)]">{t('connect.pagesFetched')}</dt>
              <dd
                data-testid="onboarding-pages-fetched"
                className="text-sm text-[var(--color-charcoal)]"
              >
                {run.pagesFetched}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-stone-mid)]">
                {t('connect.itemsExtracted')}
              </dt>
              <dd
                data-testid="onboarding-items-extracted"
                className="text-sm text-[var(--color-charcoal)]"
              >
                {run.itemsExtracted}
              </dd>
            </div>
          </dl>

          <StatusBadge
            data-testid="onboarding-domain-status"
            status={run.domainOwnershipConfirmed ? 'success' : 'pending'}
            label={
              run.domainOwnershipConfirmed
                ? t('connect.domainConfirmed')
                : t('connect.domainPending')
            }
          />

          {statusDetail && (
            <p
              data-testid="onboarding-status-detail"
              className="text-sm text-[var(--color-warm-stone-deep)]"
            >
              {t('connect.statusDetail')}: {statusDetail}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="onboarding-validate-url-btn"
              variant="secondary"
              disabled={validateUrl.isPending}
              onClick={() => validateUrl.mutate({ runId: run.id, body: { isValid: true } })}
            >
              {validateUrl.isPending ? t('connect.validating') : t('connect.validate')}
            </Button>
            <Button
              data-testid="onboarding-crawl-btn"
              variant="primary"
              disabled={crawl.isPending || !run.domainOwnershipConfirmed}
              onClick={() => crawl.mutate({ runId: run.id })}
            >
              {crawl.isPending ? t('connect.crawling') : t('connect.crawl')}
            </Button>
          </div>
        </section>
      )}

      {/* Step 2 — Review */}
      {step === 1 && (
        <section data-testid="onboarding-step-review" className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-medium text-[var(--color-charcoal)]">{t('review.title')}</h2>
            <p className="text-sm text-[var(--color-stone-mid)]">{t('review.description')}</p>
          </div>

          <StatusBadge
            data-testid="onboarding-blocking-count"
            status={blocking > 0 ? 'warning' : 'success'}
            label={
              blocking > 0
                ? t('review.blockingCount', { count: blocking })
                : t('review.blockingCleared')
            }
          />

          {itemsQuery.isLoading && (
            <LoadingState data-testid="onboarding-items-loading" title={t('states.loading')} />
          )}
          {itemsQuery.isError && (
            <ErrorState
              data-testid="onboarding-items-error"
              title={t('states.errorTitle')}
              description={t('states.errorDescription')}
              action={
                <Button
                  variant="secondary"
                  data-testid="onboarding-items-retry-btn"
                  onClick={() => void itemsQuery.refetch()}
                >
                  {t('states.retry')}
                </Button>
              }
            />
          )}
          {!itemsQuery.isLoading && !itemsQuery.isError && items.length === 0 && (
            <EmptyState
              data-testid="onboarding-items-empty"
              title={t('review.title')}
              description={t('review.empty')}
            />
          )}
          {items.length > 0 && (
            <ul data-testid="onboarding-draft-items" className="flex flex-col gap-3">
              {items.map((item) => (
                <DraftItemReview
                  key={item.id}
                  tenantId={tenantId}
                  hotelId={hotelId}
                  runId={run.id}
                  item={item}
                />
              ))}
            </ul>
          )}

          <div>
            <Button
              data-testid="onboarding-approve-btn"
              variant="primary"
              disabled={approve.isPending || blocking > 0}
              onClick={() => approve.mutate({ runId: run.id })}
            >
              {approve.isPending ? t('activate.approving') : t('activate.approve')}
            </Button>
            {blocking > 0 && (
              <p className="mt-2 text-xs text-[var(--color-warm-stone-deep)]">
                {t('activate.blockedByReview')}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Step 3 — Activate */}
      {step === 2 && (
        <section data-testid="onboarding-step-activate" className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-medium text-[var(--color-charcoal)]">{t('activate.title')}</h2>
            <p className="text-sm text-[var(--color-stone-mid)]">{t('activate.description')}</p>
          </div>

          <ActivationGateChecklist run={run} items={items} />

          {run.status === 'activated' ? (
            <StatusBadge
              data-testid="onboarding-activated-status"
              status="success"
              label={t('activate.activated')}
            />
          ) : (
            <div>
              <Button
                data-testid="onboarding-activate-btn"
                variant="primary"
                disabled={activate.isPending || blockingItemsRemaining(items) > 0}
                onClick={() => activate.mutate({ runId: run.id })}
              >
                {activate.isPending ? t('activate.activating') : t('activate.activate')}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
